import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Download, Maximize2, Minimize2 } from 'lucide-react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  MetricComparisonKind,
  QuickBooksCfoStatementsRenderContractResponse,
  QuickBooksFpaLayoutNode,
  QuickBooksFpaLayoutVersion,
  QuickBooksFpaStatementFormatting,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
  ServerApiClient,
  ServiceLineRollup,
  SuggestedServiceLineRollup,
} from '../../../data/ServerApiClient'
import { CfoInteriorPage } from './CfoInteriorPage'
import { CfoDepthSection } from './CfoDepthSection'
import { CfoControlRow, CfoStatementTable, type CfoStatementComparisonMode, type CfoStatementGlDetailCell, type CfoStatementGlDetailRequest, type CfoStatementLayoutPatch } from './CfoPagePrimitives'
import { PinToReportButton, type AddToReportPayload } from './presentation/useAddToReport'
import { ServiceLineContributionPanel } from './ServiceLineContributionPanel'
import { CfoStatementAiHeadline, type StatementAnnotations } from './CfoStatementAiHeadline'
import { CfoBooksTrustModal } from './CfoBooksTrustModal'
import { CfoStatementFlagModal, type FlagLine } from './CfoStatementFlagModal'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'

const flagSevColor = (sev: string | undefined): string =>
  sev === 'high' ? 'var(--neg)' : sev === 'medium' ? '#b8860b' : 'var(--muted, #888)'
const flagSevBg = (sev: string | undefined): string =>
  sev === 'high' ? 'rgba(192,73,47,0.14)' : sev === 'medium' ? 'rgba(184,134,11,0.14)' : 'rgba(120,120,120,0.12)'
import { ServiceLineConfigDialog } from './ServiceLineConfigDialog'

// Map a rendered statement to a board-pack "Add to report" block payload.
function statementReportPayload(statement: { statement_type?: string } | null | undefined): AddToReportPayload {
  const type = String(statement?.statement_type || 'profit_and_loss')
  if (type === 'balance_sheet') return { title: 'Balance Sheet', kind: 'statement_rollup', section: 'balanceSheet', source: { statement_type: 'balance_sheet' } }
  if (type === 'cash_flow') return { title: 'Cash Flow', kind: 'statement_rollup', section: 'cashFlow', source: { statement_type: 'cash_flow' } }
  return { title: 'Profit & Loss', kind: 'statement_rollup', section: 'profitAndLoss', source: { statement_type: 'profit_and_loss' } }
}
import { buildCfoDuckDbOpts } from './cfoDuckDb'
import { formatAsOf, formatCurrencyCompact, formatSignedCurrency, normalizeCurrencyTextCompact, trimStr } from './cfoFormat'
import { CFO_SURFACES } from './cfoTelemetry'
import { cfoAnalyticsState, useCfoBootstrap } from './useCfoBootstrap'
import { CfoShellSelectChip, type CfoShellSelectOption } from './CfoPageShell'
import type { CfoNavProfile } from './cfoNav'
import {
  buildCfoCoreExcelFileName,
  buildCfoCoreThreeStatementExcelSnapshot,
  type CfoCoreExcelStatementType,
} from '../financialStatementExcelExport'
import { normalizeQuickBooksFpaSortOrder } from '../quickBooksFpaLayoutRows'
import { setPendingCfoGlDetailHandoff } from './cfoGlDetailHandoff'
import { CfoGlDetailModal, buildGlDetailHandoff } from './CfoGlDetailModal'
import { buildCfoInstantCardFromContext } from './analysis/instantCards'
import { useCfoAnalysis, type CfoAnalysisGlDetailTarget } from './analysis'
import type { CfoInstantCard } from './analysis/types'

const SURFACE = CFO_SURFACES.financials
const EXCEL_STATEMENT_TYPES: CfoCoreExcelStatementType[] = ['profit_and_loss', 'balance_sheet', 'cash_flow']
const FINANCIALS_STATEMENT_ROLLUP_MAX_DEPTH = 3
type StatementKind = 'profit_and_loss' | 'balance_sheet' | 'cash_flow'
type UnitMode = 'currency' | 'percent' | 'currency_percent'
type PeriodDisplayMode = 'months' | 'quarters' | 'years'
type StatementViewMode = 'periods' | 'compare'
type FinancialsComparisonMode = CfoStatementComparisonMode

const STATEMENT_OPTIONS: CfoShellSelectOption[] = [
  { id: 'profit_and_loss', label: 'Profit & Loss' },
  { id: 'balance_sheet', label: 'Balance sheet' },
  { id: 'cash_flow', label: 'Cash flow' },
]

const statementFromRenderContract = (
  contract: QuickBooksCfoStatementsRenderContractResponse,
  statementKind: StatementKind,
): QuickBooksFpaStatementRender => {
  if (statementKind === 'balance_sheet') return contract.balance_sheet
  if (statementKind === 'cash_flow') return contract.cash_flow
  return contract.profit_and_loss
}

const STATEMENT_VIEW_OPTIONS: CfoShellSelectOption[] = [
  { id: 'periods', label: 'Periods' },
  { id: 'compare', label: 'Compare' },
]

const PERIOD_DISPLAY_OPTIONS: CfoShellSelectOption[] = [
  { id: 'months', label: 'Months' },
  { id: 'quarters', label: 'Quarters' },
  { id: 'years', label: 'Years' },
]

const UNIT_OPTIONS: CfoShellSelectOption[] = [
  { id: 'currency', label: 'Amount' },
  { id: 'percent', label: '% of sales' },
  { id: 'currency_percent', label: 'Amount and %' },
]

const COMPARISON_OPTIONS: Array<{ id: MetricComparisonKind; label: string; statementMode: FinancialsComparisonMode; helperLabel: string }> = [
  { id: 'mom', label: 'vs. prior month', statementMode: 'prior_month', helperLabel: 'Month-over-month' },
  { id: 'qoq', label: 'vs. prior quarter', statementMode: 'prior_quarter', helperLabel: 'Quarter-over-quarter' },
  { id: 'yoy', label: 'vs. prior year', statementMode: 'prior_year', helperLabel: 'Same month last year' },
  { id: 'l3m_vs_prior3m', label: 'last 3mo vs prior 3mo', statementMode: 'rolling_3_vs_prior_3', helperLabel: 'Six-month comparison' },
  { id: 'l12m_vs_prior12m', label: 'last 12mo vs prior 12mo', statementMode: 'rolling_12_vs_prior_12', helperLabel: '24-month comparison' },
]

const normalizeFinancialPeriod = (period: string | null | undefined): string => {
  const match = /^(\d{4})-(\d{2})/.exec(String(period || '').trim())
  return match ? `${match[1]}-${match[2]}-01` : ''
}

const mergeFinancialPeriodOptions = (
  primary: CfoShellSelectOption[],
  secondary: CfoShellSelectOption[],
): CfoShellSelectOption[] => {
  const byId = new Map<string, CfoShellSelectOption>()
  for (const option of secondary) {
    const id = normalizeFinancialPeriod(option.id)
    if (id) byId.set(id, { id, label: option.label || formatAsOf(id) })
  }
  for (const option of primary) {
    const id = normalizeFinancialPeriod(option.id)
    if (id) byId.set(id, { id, label: option.label || formatAsOf(id) })
  }
  return [...byId.values()].sort((a, b) => b.id.localeCompare(a.id))
}

const periodDisplayStatementMode = (mode: PeriodDisplayMode): FinancialsComparisonMode => {
  if (mode === 'quarters') return 'rolling_quarters'
  if (mode === 'years') return 'rolling_years'
  return 'rolling_12'
}

const periodDisplayBounds = (mode: PeriodDisplayMode) => {
  if (mode === 'years') return { min: 1, max: 10 }
  if (mode === 'quarters') return { min: 1, max: 16 }
  return { min: 1, max: 60 }
}

const clampPeriodCount = (mode: PeriodDisplayMode, count: number) => {
  const bounds = periodDisplayBounds(mode)
  const normalized = Number.isFinite(count) ? Math.round(count) : 1
  return Math.max(bounds.min, Math.min(bounds.max, normalized))
}

const clampStatementRollupDepth = (depth: number) =>
  Math.max(0, Math.min(FINANCIALS_STATEMENT_ROLLUP_MAX_DEPTH, Number.isFinite(depth) ? Math.round(depth) : 1))

const periodDisplayFetchMonths = (mode: PeriodDisplayMode, count: number) => {
  if (mode === 'years') return Math.max(24, count * 12)
  if (mode === 'quarters') return Math.max(24, count * 3)
  return Math.max(24, count)
}

const normalizeStatementRowLabel = (label: string): string => String(label || '').trim().replace(/\s+/g, ' ')

const financialsSourceNodeId = (nodeId: string | null | undefined): string => {
  const sourceMatch = /:source:(.+)$/.exec(String(nodeId || ''))
  return sourceMatch?.[1] || String(nodeId || '')
}

export const statementCurrentIndex = (statement: QuickBooksFpaStatementRender, endPeriod: string | null | undefined): number => {
  const normalizedEnd = normalizeFinancialPeriod(endPeriod)
  if (normalizedEnd) {
    const index = statement.periods.findIndex((period) => normalizeFinancialPeriod(period) === normalizedEnd)
    if (index >= 0) return index
  }
  return statement.periods.length - 1
}

// A balance-sheet figure is a point-in-time balance, while GL detail is a
// transaction stream. When an anomaly is attached to a balance, the useful
// drill is the month in which that balance materially moved into place—not a
// later month that happens to contain routine interest or bank activity.
//
// We only infer this when the rendered history visibly establishes the move:
// a leading opening balance may predate the visible window, in which case the
// selected period remains the honest scope.
export const balanceSheetFlagDrilldownPeriod = (
  statement: QuickBooksFpaStatementRender | null,
  row: QuickBooksFpaStatementRow | null | undefined,
  endPeriod: string | null | undefined,
): string | null => {
  if (!statement || statement.statement_type !== 'balance_sheet' || !row) return null
  const currentIndex = statementCurrentIndex(statement, endPeriod)
  if (currentIndex < 1) return null

  let largestChangeIndex = -1
  let largestChange = 0
  for (let index = 1; index <= currentIndex; index += 1) {
    const currentRaw = row.values[index]
    const priorRaw = row.values[index - 1]
    if (currentRaw == null && priorRaw == null) continue
    const current = Number(currentRaw ?? 0)
    const prior = Number(priorRaw ?? 0)
    if (!Number.isFinite(current) || !Number.isFinite(prior)) continue
    const change = Math.abs(current - prior)
    if (change > largestChange) {
      largestChange = change
      largestChangeIndex = index
    }
  }
  const endingBalance = Number(row.values[currentIndex] ?? 0)
  // Ignore routine accretion (for example, monthly interest): it cannot
  // honestly explain a flagged six-figure ending balance. The server already
  // materiality-gates flags; this guard only decides whether visible history
  // identifies a causal transaction month.
  const materialChange = Math.max(250, Number.isFinite(endingBalance) ? Math.abs(endingBalance) * 0.02 : 250)
  return largestChangeIndex >= 0 && largestChange >= materialChange
    ? normalizeFinancialPeriod(statement.periods[largestChangeIndex]) || null
    : null
}

const statementValue = (row: QuickBooksFpaStatementRow, index: number): number | null => {
  if (index < 0 || index >= row.values.length) return null
  const value = Number(row.values[index] || 0)
  return Number.isFinite(value) ? value : null
}

const explicitTotalLabelForStatementRow = (row: QuickBooksFpaStatementRow): string => {
  const label = normalizeStatementRowLabel(row.label).toLowerCase().replace(/^total\s+/, '')
  return label ? `total ${label}` : ''
}

const findExplicitStatementTotalRow = (
  statement: QuickBooksFpaStatementRender,
  row: QuickBooksFpaStatementRow,
): QuickBooksFpaStatementRow | null => {
  const totalLabel = explicitTotalLabelForStatementRow(row)
  if (!totalLabel) return null
  const rowIndex = statement.rows.findIndex((candidate) => candidate.node_id === row.node_id)
  const candidates = rowIndex >= 0 ? statement.rows.slice(rowIndex + 1) : statement.rows
  return candidates.find((candidate) => {
    if (candidate.node_id === row.node_id) return false
    if (!(candidate.is_total || candidate.row_type === 'group' || candidate.row_type === 'formula')) return false
    return normalizeStatementRowLabel(candidate.label).toLowerCase().replace(/^total\s+/, 'total ') === totalLabel
  }) || null
}

export const statementRolledValue = (statement: QuickBooksFpaStatementRender, row: QuickBooksFpaStatementRow, index: number): number | null => {
  const direct = statementValue(row, index)
  if (direct != null && Math.abs(direct) >= 0.005) return direct
  if (row.row_type !== 'heading' && row.row_type !== 'group') return direct
  const explicitTotal = findExplicitStatementTotalRow(statement, row)
  if (explicitTotal) {
    const totalValue = statementValue(explicitTotal, index)
    if (totalValue != null && Math.abs(totalValue) >= 0.005) return totalValue
  }
  const rowIndex = statement.rows.findIndex((candidate) => candidate.node_id === row.node_id)
  if (rowIndex < 0) return direct
  const rowLevel = Number(row.level || 0)
  let total = 0
  let foundChild = false
  for (let indexInRows = rowIndex + 1; indexInRows < statement.rows.length; indexInRows += 1) {
    const child = statement.rows[indexInRows]
    if (Number(child.level || 0) <= rowLevel) break
    if (Number(child.level || 0) !== rowLevel + 1) continue
    const value = statementRolledValue(statement, child, index)
    if (value == null) continue
    total += value
    foundChild = true
  }
  return foundChild ? total : direct
}

const comparisonPeriodOffset = (comparisonKind: MetricComparisonKind): number | null => {
  if (comparisonKind === 'mom') return 1
  if (comparisonKind === 'qoq') return 3
  if (comparisonKind === 'yoy') return 12
  return null
}

const periodMonthStart = (period: string | null | undefined): string | null => {
  const normalized = normalizeFinancialPeriod(period)
  return normalized || null
}

const statementWindowValue = (
  statement: QuickBooksFpaStatementRender,
  row: QuickBooksFpaStatementRow,
  indices: number[],
): number | null => {
  if (!indices.length) return null
  let total = 0
  let found = false
  for (const index of indices) {
    const value = statementRolledValue(statement, row, index)
    if (value == null) continue
    total += value
    found = true
  }
  return found ? total : null
}

export const statementComparisonWindow = (
  statement: QuickBooksFpaStatementRender,
  endPeriod: string | null | undefined,
  comparisonKind: MetricComparisonKind,
) => {
  const currentIndex = statementCurrentIndex(statement, endPeriod)
  const single = (priorIndex: number) => ({
    currentIndices: currentIndex >= 0 ? [currentIndex] : [],
    priorIndices: priorIndex >= 0 ? [priorIndex] : [],
  })
  if (comparisonKind === 'qoq') return single(currentIndex - 3)
  if (comparisonKind === 'yoy') return single(currentIndex - 12)
  if (comparisonKind === 'l3m_vs_prior3m') {
    const currentStart = Math.max(0, currentIndex - 2)
    const priorStart = Math.max(0, currentStart - 3)
    return {
      currentIndices: Array.from({ length: currentIndex - currentStart + 1 }, (_, index) => currentStart + index),
      priorIndices: Array.from({ length: currentStart - priorStart }, (_, index) => priorStart + index),
    }
  }
  if (comparisonKind === 'l12m_vs_prior12m') {
    const currentStart = Math.max(0, currentIndex - 11)
    const priorStart = Math.max(0, currentStart - 12)
    return {
      currentIndices: Array.from({ length: currentIndex - currentStart + 1 }, (_, index) => currentStart + index),
      priorIndices: Array.from({ length: currentStart - priorStart }, (_, index) => priorStart + index),
    }
  }
  return single(currentIndex - 1)
}

type FinancialsInsightRow = {
  row: QuickBooksFpaStatementRow
  currentValue: number
  priorValue: number
  delta: number
  parentLabel: string | null
}

const findTopParentLabel = (
  rowsById: Map<string, QuickBooksFpaStatementRow>,
  row: QuickBooksFpaStatementRow,
): string | null => {
  let current: QuickBooksFpaStatementRow | undefined = row
  let parent: QuickBooksFpaStatementRow | undefined
  while (current?.parent_node_id) {
    const next = rowsById.get(current.parent_node_id)
    if (!next) break
    parent = next
    current = next
  }
  return parent ? normalizeStatementRowLabel(parent.label) : null
}

const rankedStatementInsightRows = (
  statement: QuickBooksFpaStatementRender | null,
  endPeriod: string | null | undefined,
  comparisonKind: MetricComparisonKind,
  expanded: boolean,
): FinancialsInsightRow[] => {
  if (!statement) return []
  const { currentIndices, priorIndices } = statementComparisonWindow(statement, endPeriod, comparisonKind)
  if (!currentIndices.length || !priorIndices.length) return []
  const rowsById = new Map(statement.rows.map((row) => [row.node_id, row]))
  return statement.rows
    .filter((row) => {
      const level = Number(row.level || 0)
      if (expanded) return level > 0 && row.row_type !== 'heading' && rowHasAnyValue(row)
      return level === 0 || row.is_total || row.row_type === 'formula'
    })
    .map((row): FinancialsInsightRow | null => {
      const currentValue = statementWindowValue(statement, row, currentIndices)
      const priorValue = statementWindowValue(statement, row, priorIndices)
      if (currentValue == null || priorValue == null) return null
      const delta = currentValue - priorValue
      if (!Number.isFinite(delta) || Math.abs(delta) < 0.005) return null
      return {
        row,
        currentValue,
        priorValue,
        delta,
        parentLabel: expanded ? findTopParentLabel(rowsById, row) : null,
      }
    })
    .filter((row): row is FinancialsInsightRow => Boolean(row))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
}

const rowHasAnyValue = (row: QuickBooksFpaStatementRow): boolean =>
  row.values.some((value) => Number.isFinite(Number(value)) && Math.abs(Number(value)) >= 0.005)

const statementPeriodsFingerprint = (statement: QuickBooksFpaStatementRender | null): string =>
  statement
    ? [
        statement.statement_type || 'statement',
        statement.periods.slice(-3).join(','),
        statement.rows.length,
        statement.rows.slice(0, 20).map((row) => `${row.node_id}:${row.values.slice(-2).join(',')}`).join('|'),
      ].join(':')
    : ''

const statementRowTrend = (
  statement: QuickBooksFpaStatementRender,
  row: QuickBooksFpaStatementRow,
  endPeriod: string | null | undefined,
  metricLabel: string,
) => {
  const currentIndex = statementCurrentIndex(statement, endPeriod)
  const start = Math.max(0, currentIndex - 11)
  const points = statement.periods.slice(start, currentIndex + 1).map((period, offset) => {
    const index = start + offset
    const value = statementRolledValue(statement, row, index)
    return {
      label: formatAsOf(period),
      period,
      rawValue: value,
      value: value == null ? '-' : formatCurrencyCompact(value),
    }
  })
  return {
    label: points.length >= 12 ? '12-month trend' : 'Available trend evidence',
    points,
    unavailable_reason: points.length >= 12 ? null : `${metricLabel} has ${points.length} rendered monthly points in this statement view.`,
  }
}

const pctChangeText = (current: number | null, prior: number | null): string => {
  if (current == null || prior == null || Math.abs(prior) < 0.005) return '-'
  const pct = (current - prior) / Math.abs(prior)
  const sign = pct > 0 ? '+' : pct < 0 ? '-' : ''
  return `${sign}${Math.abs(pct * 100).toFixed(1)}%`
}

const statementRowFinancialRole = (
  statement: QuickBooksFpaStatementRender,
  row: QuickBooksFpaStatementRow,
): 'benefit' | 'cost' | 'neutral' => {
  const text = (candidate: QuickBooksFpaStatementRow): string => [
    candidate.label,
    candidate.semantic_key,
    candidate.qbo_classification,
    candidate.qbo_account_type,
    candidate.qbo_account_sub_type,
  ].filter(Boolean).join(' ').toLowerCase()
  const explicitRole = (candidate: QuickBooksFpaStatementRow): 'benefit' | 'cost' | null => {
    const metadata = text(candidate)
    const classification = String(candidate.qbo_classification || '').toLowerCase()
    const accountType = String(candidate.qbo_account_type || '').toLowerCase()
    const normalBalance = String(candidate.normal_balance || '').toLowerCase()
    if (
      classification === 'expense' ||
      /expense|cost|cogs/.test(accountType) ||
      (statement.statement_type === 'profit_and_loss' && normalBalance === 'debit')
    ) return 'cost'
    if (
      classification === 'revenue' ||
      classification === 'income' ||
      /income|revenue/.test(accountType) ||
      (statement.statement_type === 'profit_and_loss' && normalBalance === 'credit')
    ) return 'benefit'
    if (/expense|cost of|cogs|payroll|wage|rent|fee|tax|interest/.test(metadata)) return 'cost'
    if (/revenue|sales|gross profit|net income|operating income/.test(metadata)) return 'benefit'
    return null
  }

  const direct = explicitRole(row)
  if (direct) return direct

  const rowsById = new Map(statement.rows.map((candidate) => [candidate.node_id, candidate]))
  let ancestor = row.parent_node_id ? rowsById.get(row.parent_node_id) : undefined
  while (ancestor) {
    const role = explicitRole(ancestor)
    if (role) return role
    ancestor = ancestor.parent_node_id ? rowsById.get(ancestor.parent_node_id) : undefined
  }

  const descendants = statement.rows.filter((candidate) => {
    let parentId = candidate.parent_node_id
    while (parentId) {
      if (parentId === row.node_id) return true
      parentId = rowsById.get(parentId)?.parent_node_id
    }
    return false
  })
  const descendantRoles = new Set(descendants.map(explicitRole).filter(Boolean))
  if (descendantRoles.size === 1) return descendantRoles.has('cost') ? 'cost' : 'benefit'

  return 'neutral'
}

const statementRowTone = (
  statement: QuickBooksFpaStatementRender,
  row: QuickBooksFpaStatementRow,
  delta: number | null,
): CfoInstantCard['primaryMetric']['tone'] => {
  if (delta == null || Math.abs(delta) < 0.005) return 'neutral'
  const role = statementRowFinancialRole(statement, row)
  if (role === 'cost') return delta > 0 ? 'bad' : 'good'
  if (role === 'benefit') return delta > 0 ? 'good' : 'bad'
  return 'neutral'
}

const comparisonRowFromValues = (
  label: string,
  statement: QuickBooksFpaStatementRender,
  row: QuickBooksFpaStatementRow,
  current: number | null,
  prior: number | null,
) => {
  const delta = current != null && prior != null ? current - prior : null
  return {
    label,
    current: current == null ? '-' : formatCurrencyCompact(current),
    prior: prior == null ? '-' : formatCurrencyCompact(prior),
    change: pctChangeText(current, prior),
    tone: statementRowTone(statement, row, delta),
    unavailableReason: current == null || prior == null ? 'Not available in the rendered statement window.' : null,
  }
}

const statementRowComparisonRows = (
  statement: QuickBooksFpaStatementRender,
  row: QuickBooksFpaStatementRow,
  endPeriod: string | null | undefined,
) => {
  const currentIndex = statementCurrentIndex(statement, endPeriod)
  const valueAt = (index: number): number | null => (index >= 0 && index < statement.periods.length ? statementRolledValue(statement, row, index) : null)
  const windowValue = (indices: number[]): number | null => statementWindowValue(statement, row, indices.filter((index) => index >= 0 && index < statement.periods.length))
  const currentPeriod = normalizeFinancialPeriod(statement.periods[currentIndex])
  const [, currentYearRaw, currentMonthRaw] = /^(\d{4})-(\d{2})/.exec(currentPeriod) || []
  const currentYear = Number(currentYearRaw)
  const currentMonth = Number(currentMonthRaw)
  const ytdIndices = statement.periods
    .map((period, index) => ({ period: normalizeFinancialPeriod(period), index }))
    .filter(({ period, index }) => {
      const match = /^(\d{4})-(\d{2})/.exec(period)
      return match && Number(match[1]) === currentYear && Number(match[2]) <= currentMonth && index <= currentIndex
    })
    .map(({ index }) => index)
  const priorYtdIndices = statement.periods
    .map((period, index) => ({ period: normalizeFinancialPeriod(period), index }))
    .filter(({ period }) => {
      const match = /^(\d{4})-(\d{2})/.exec(period)
      return match && Number(match[1]) === currentYear - 1 && Number(match[2]) <= currentMonth
    })
    .map(({ index }) => index)
  return [
    comparisonRowFromValues('Current month', statement, row, valueAt(currentIndex), valueAt(currentIndex - 1)),
    comparisonRowFromValues(
      'Last 3 months',
      statement,
      row,
      windowValue([currentIndex - 2, currentIndex - 1, currentIndex]),
      windowValue([currentIndex - 5, currentIndex - 4, currentIndex - 3]),
    ),
    comparisonRowFromValues('YTD', statement, row, windowValue(ytdIndices), windowValue(priorYtdIndices)),
    comparisonRowFromValues('Prior year', statement, row, valueAt(currentIndex), valueAt(currentIndex - 12)),
  ]
}

const statementRowHasChildren = (
  statement: QuickBooksFpaStatementRender,
  row: QuickBooksFpaStatementRow,
): boolean => statement.rows.some((candidate) => candidate.parent_node_id === row.node_id)

export const buildFinancialsInstantCards = (
  statement: QuickBooksFpaStatementRender | null,
  endPeriod: string | null | undefined,
  comparisonKind: MetricComparisonKind,
  comparisonLabel: string,
  asOfLabel: string,
): CfoInstantCard[] => {
  if (!statement) return []
  const sourceFingerprint = statementPeriodsFingerprint(statement)
  const rollups = rankedStatementInsightRows(statement, endPeriod, comparisonKind, false).slice(0, 4)
  const expanded = rankedStatementInsightRows(statement, endPeriod, comparisonKind, true).slice(0, 4)
  const seen = new Set<string>()
  return [...rollups, ...expanded].flatMap((item) => {
    const row = item.row
    const label = normalizeStatementRowLabel(row.label)
    const key = `${statement.statement_type}:${row.node_id}`
    if (!label || seen.has(key)) return []
    seen.add(key)
    const hasChildren = statementRowHasChildren(statement, row)
    return buildCfoInstantCardFromContext({
      context: {
        workbookId: null,
        pageId: 'financials',
        activeSection: SURFACE,
        period: normalizeFinancialPeriod(endPeriod) || endPeriod || null,
        comparison: comparisonKind,
        asOfLabel,
        metricId: row.node_id || label,
        metricLabel: label,
        sourceComponent: 'statement_row',
        sourceTables: ['fg_cfo_ai_statement_monthly_activity', 'fg_cfo_ai_gl_detail'],
        selection: {
          selection_key: `financials:${key}`,
          row_key: row.node_id,
          row_node_id: row.node_id,
          row_label: label,
          statement_type: statement.statement_type,
          comparison_label: comparisonLabel,
          current_value: item.currentValue,
          prior_value: item.priorValue,
          delta: item.delta,
          metric_delta: item.delta,
          metric_tone: statementRowTone(statement, row, item.delta),
          value: item.delta,
          parent_label: item.parentLabel,
          source_fingerprint: sourceFingerprint,
          artifact_ids: [statement.statement_type, 'statement_render_contract'].filter(Boolean),
          instant_card_trend: statementRowTrend(statement, row, endPeriod, label),
          instant_card_comparisons: statementRowComparisonRows(statement, row, endPeriod),
          instant_card_insights: [
            `${label} moved ${formatSignedCurrency(item.delta)} ${comparisonLabel}; it is one of the largest statement movements in this rendered view.`,
          ],
          instant_card_prescriptive_actions: [
            hasChildren
              ? `Inspect the child accounts and GL detail behind ${label} before changing the forecast or monthly narrative.`
              : `Inspect bounded GL detail behind ${label} before changing the forecast or monthly narrative.`,
          ],
          instant_card_next_steps: [
            ...(hasChildren ? [{
              kind: 'drilldown',
              label: `Expand ${label}`,
              prompt: `Show the bounded child-account rollup for ${label}.`,
              target: { surface: SURFACE, metricId: row.node_id || label },
            }] : []),
            {
              kind: 'transactions',
              label: 'Open GL detail',
              prompt: `Open GL detail for ${label}.`,
              target: { surface: 'cfo_gl_detail', metricId: row.node_id || label },
            },
            {
              kind: 'compare',
              label: 'Compare periods',
              prompt: `Compare ${label} across current month, last 3 months, YTD, and prior year.`,
              target: { surface: SURFACE, metricId: row.node_id || label },
            },
          ],
        },
        grounding: null,
        instantCard: null,
      },
      visual: null,
    })
  })
}

const buildFinancialsRowAnalysisSelection = (
  statement: QuickBooksFpaStatementRender | null,
  row: QuickBooksFpaStatementRow,
  endPeriod: string | null | undefined,
  comparisonKind: MetricComparisonKind,
  comparisonLabel: string,
  statementKind: StatementKind,
): Record<string, any> | null => {
  if (!statement) return null
  const label = normalizeStatementRowLabel(row.label)
  if (!label) return null
  const { currentIndices, priorIndices } = statementComparisonWindow(statement, endPeriod, comparisonKind)
  const currentValue = currentIndices.length ? statementWindowValue(statement, row, currentIndices) : null
  const priorValue = priorIndices.length ? statementWindowValue(statement, row, priorIndices) : null
  const delta = currentValue != null && priorValue != null ? currentValue - priorValue : null
  const rowsById = new Map(statement.rows.map((candidate) => [candidate.node_id, candidate]))
  const metricId = row.node_id || label
  const hasChildren = statementRowHasChildren(statement, row)
  return {
    source_component: 'statement_row',
    metric_id: metricId,
    metric_label: label,
    source_tables: ['fg_cfo_ai_statement_monthly_activity', 'fg_cfo_ai_gl_detail'],
    selection_key: `financials:${statement.statement_type}:${metricId}`,
    row_key: metricId,
    row_node_id: row.node_id,
    row_label: label,
    statement_type: statement.statement_type || statementKind,
    comparison_label: comparisonLabel,
    current_value: currentValue,
    prior_value: priorValue,
    delta,
    metric_delta: delta,
    metric_tone: statementRowTone(statement, row, delta),
    value: delta ?? currentValue,
    parent_label: findTopParentLabel(rowsById, row),
    source_fingerprint: statementPeriodsFingerprint(statement),
    artifact_ids: [statement.statement_type || statementKind, 'statement_render_contract'].filter(Boolean),
    instant_card_trend: statementRowTrend(statement, row, endPeriod, label),
    instant_card_comparisons: statementRowComparisonRows(statement, row, endPeriod),
    instant_card_insights: [
      delta == null
        ? `${label} is selected from the rendered ${statementKind.replace(/_/g, ' ')} statement.`
        : `${label} moved ${formatSignedCurrency(delta)} ${comparisonLabel}; use the row card to inspect the trend and comparison windows before writing a narrative.`,
    ],
    instant_card_prescriptive_actions: [
      hasChildren
        ? `Inspect child accounts and bounded GL detail for ${label} before changing the forecast or monthly narrative.`
        : `Inspect bounded GL detail for ${label} before changing the forecast or monthly narrative.`,
    ],
    instant_card_next_steps: [
      ...(hasChildren ? [{
        kind: 'drilldown',
        label: `Expand ${label}`,
        prompt: `Show the bounded child-account rollup for ${label}.`,
        target: { surface: SURFACE, metricId },
      }] : []),
      {
        kind: 'transactions',
        label: 'Open GL detail',
        prompt: `Open GL detail for ${label}.`,
        target: { surface: 'cfo_gl_detail', metricId },
      },
      {
        kind: 'compare',
        label: 'Compare periods',
        prompt: `Compare ${label} across current month, last 3 months, YTD, and prior year.`,
        target: { surface: SURFACE, metricId },
      },
    ],
  }
}

export const buildFinancialsCellAnalysisSelection = (
  statement: QuickBooksFpaStatementRender | null,
  cell: CfoStatementGlDetailCell,
  endPeriod: string | null | undefined,
  comparisonKind: MetricComparisonKind,
  comparisonLabel: string,
  statementKind: StatementKind,
): Record<string, any> | null => {
  if (!statement) return null
  const row = statement.rows.find((candidate) => (
    candidate.node_id === cell.nodeId ||
    financialsSourceNodeId(candidate.node_id) === cell.nodeId
  ))
  if (!row) return null
  const base = buildFinancialsRowAnalysisSelection(
    statement,
    row,
    endPeriod,
    comparisonKind,
    comparisonLabel,
    statementKind,
  )
  if (!base) return null
  const cellPeriod = normalizeFinancialPeriod(cell.period) || cell.period || null
  const cellValue = Number(cell.value)
  const cellValueText = Number.isFinite(cellValue) ? formatCurrencyCompact(cellValue) : '—'
  const metricId = String(base.metric_id || row.node_id || cell.nodeId || base.metric_label)
  const metricLabel = String(base.metric_label || cell.rowLabel || row.label || 'Statement cell')
  const hasChildren = statementRowHasChildren(statement, row)
  return {
    ...base,
    source_component: 'statement_cell',
    metric_id: metricId,
    metric_label: metricLabel,
    selection_key: `financials-cell:${statement.statement_type}:${metricId}:${cell.columnKey}:${cellPeriod || 'row'}`,
    cell_node_id: cell.nodeId,
    cell_period: cellPeriod,
    cell_column_key: cell.columnKey,
    cell_column_label: cell.columnLabel,
    cell_value: Number.isFinite(cellValue) ? cellValue : null,
    value: Number.isFinite(cellValue) ? cellValue : base.value,
    account_id: cell.accountId || row.account_id || null,
    account_number: cell.accountNumber || row.account_number || null,
    account_name: cell.accountName || row.label || null,
    instant_card_insights: [
      `${metricLabel} is ${cellValueText} for ${cell.columnLabel}; use the row trend and comparison windows to decide whether it is isolated or part of the broader movement.`,
    ],
    instant_card_prescriptive_actions: [
      `Open bounded GL detail for ${metricLabel} in ${cell.columnLabel} before changing the forecast or monthly narrative.`,
    ],
    instant_card_next_steps: [
      {
        kind: 'transactions',
        label: 'Open GL detail',
        prompt: `Open GL detail for ${metricLabel} in ${cell.columnLabel}.`,
        target: { surface: 'cfo_gl_detail', metricId, accountId: cell.accountId || row.account_id || null, period: cellPeriod },
      },
      ...(hasChildren ? [{
        kind: 'drilldown',
        label: `Expand ${metricLabel}`,
        prompt: `Show the bounded child-account rollup for ${metricLabel}.`,
        target: { surface: SURFACE, metricId, accountId: cell.accountId || row.account_id || null },
      }] : []),
      {
        kind: 'compare',
        label: 'Compare periods',
        prompt: `Compare ${metricLabel} across current month, last 3 months, YTD, and prior year.`,
        target: { surface: SURFACE, metricId },
      },
    ],
  }
}

const buildFinancialsAiInsights = (
  statement: QuickBooksFpaStatementRender | null,
  endPeriod: string | null | undefined,
  comparisonKind: MetricComparisonKind,
  comparisonLabel: string,
): string[] => {
  const rollups = rankedStatementInsightRows(statement, endPeriod, comparisonKind, false)
  const expandedRows = rankedStatementInsightRows(statement, endPeriod, comparisonKind, true)
  const topRollup = rollups[0] || null
  const topExpanded = expandedRows.find((row) => row.parentLabel && (!topRollup || row.parentLabel === normalizeStatementRowLabel(topRollup.row.label))) || expandedRows[0] || null
  if (!topRollup && !topExpanded) return []
  const insights: string[] = []
  if (topRollup) {
    const label = normalizeStatementRowLabel(topRollup.row.label)
    insights.push(
      `${label} moved ${formatSignedCurrency(topRollup.delta)} ${comparisonLabel}; start by expanding ${label} because it is the largest rolled-up movement in the statement.`,
    )
  }
  if (topExpanded) {
    const label = normalizeStatementRowLabel(topExpanded.row.label)
    const parent = topExpanded.parentLabel ? `${topExpanded.parentLabel} / ` : ''
    insights.push(
      `In the fully expanded rows, ${parent}${label} moved ${formatSignedCurrency(topExpanded.delta)} (${formatCurrencyCompact(topExpanded.currentValue)} vs. ${formatCurrencyCompact(topExpanded.priorValue)}), making it the first detail area to inspect.`,
    )
  }
  if (topRollup || topExpanded) {
    const target = topRollup ? normalizeStatementRowLabel(topRollup.row.label) : topExpanded ? normalizeStatementRowLabel(topExpanded.row.label) : 'the largest movers'
    insights.push(
      `For a deeper dive, open Financials full screen and compare ${target} across the selected ${comparisonLabel} columns before drilling into GL detail.`,
    )
  }
  return insights
}

const buildStatementAnalysisSelection = (
  statement: QuickBooksFpaStatementRender | null,
  endPeriod: string | null | undefined,
  comparisonKind: MetricComparisonKind,
  comparisonLabel: string,
  statementKind: StatementKind,
) => {
  if (!statement) {
    return {
      evidence_scope: 'financial_statement',
      statement_type: statementKind,
      comparison: comparisonKind,
      comparison_label: comparisonLabel,
      statement_view_mode: 'rolled_up_and_fully_expanded',
      analysis_instruction: 'Analyze the rendered financial statement rollup first, then inspect fully expanded statement activity for the selected comparison window before suggesting next drilldowns.',
    }
  }
  const currentIndex = statementCurrentIndex(statement, endPeriod)
  const offset = comparisonPeriodOffset(comparisonKind)
  const priorIndex = offset == null ? null : currentIndex - offset
  const rolledUpRows = statement.rows
    .filter((row) => Number(row.level || 0) === 0 || row.is_total || row.row_type === 'formula')
    .map((row) => {
      const currentValue = statementRolledValue(statement, row, currentIndex)
      const priorValue = priorIndex != null && priorIndex >= 0 ? statementRolledValue(statement, row, priorIndex) : null
      return {
        node_id: row.node_id,
        label: normalizeStatementRowLabel(row.label),
        row_type: row.row_type,
        level: row.level,
        is_total: row.is_total,
        current_value: currentValue,
        prior_value: priorValue,
        delta_abs: currentValue != null && priorValue != null ? currentValue - priorValue : null,
      }
    })
    .slice(0, 24)
  return {
    evidence_scope: 'financial_statement',
    statement_type: statement.statement_type || statementKind,
    comparison: comparisonKind,
    comparison_label: comparisonLabel,
    statement_view_mode: 'rolled_up_and_fully_expanded',
    current_period_end: statement.periods[currentIndex] || endPeriod || null,
    current_period_start: periodMonthStart(statement.periods[currentIndex] || endPeriod),
    prior_period_end: priorIndex != null && priorIndex >= 0 ? statement.periods[priorIndex] || null : null,
    prior_period_start: priorIndex != null && priorIndex >= 0 ? periodMonthStart(statement.periods[priorIndex]) : null,
    available_periods: statement.periods,
    rolled_up_rows: rolledUpRows,
    expanded_row_count: statement.rows.length,
    expanded_row_labels_sample: statement.rows
      .filter((row) => Number(row.level || 0) > 0 && row.row_type !== 'heading')
      .slice(0, 30)
      .map((row) => ({
        node_id: row.node_id,
        label: normalizeStatementRowLabel(row.label),
        parent_node_id: row.parent_node_id || null,
        level: row.level,
        row_type: row.row_type,
      })),
    analysis_instruction: 'Lead with the rolled-up statement movement, then use fully expanded account/category rows from fg_cfo_ai_statement_monthly_activity for the same comparison window. Suggest concrete rows to expand and mention full screen when deeper inspection would help.',
  }
}

const EXCEL_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function downloadWorkbookBytes(args: { fileName: string; contentType?: string | null; bytes: Uint8Array }): void {
  if (typeof document === 'undefined') return
  const blob = new Blob([args.bytes as BlobPart], { type: args.contentType || EXCEL_CONTENT_TYPE })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = args.fileName || 'cfo-core-export.xlsx'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function downloadBase64Workbook(args: { fileName: string; contentType?: string | null; packageBase64: string }): void {
  const binary = atob(args.packageBase64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  downloadWorkbookBytes({ fileName: args.fileName, contentType: args.contentType, bytes })
}

function normalizeStatementLayoutPatch(patch: CfoStatementLayoutPatch): CfoStatementLayoutPatch {
  const nodes = patch.nodes?.map((nodePatch) => {
    if (!Object.prototype.hasOwnProperty.call(nodePatch, 'sort_order')) return nodePatch
    const numeric = Number(nodePatch.sort_order)
    if (!Number.isFinite(numeric)) {
      const { sort_order: _sortOrder, ...rest } = nodePatch
      return rest
    }
    return { ...nodePatch, sort_order: normalizeQuickBooksFpaSortOrder(numeric) }
  })
  return {
    ...patch,
    ...(nodes ? { nodes } : {}),
  }
}

function applyLayoutPatchLocal(layout: QuickBooksFpaLayoutVersion, patch: CfoStatementLayoutPatch): QuickBooksFpaLayoutVersion {
  let nodes = layout.nodes.map((node) => ({ ...node }))
  const indexById = new Map(nodes.map((node, index) => [node.id, index]))
  for (const nodePatch of patch.nodes || []) {
    const id = String(nodePatch.id || '').trim()
    if (!id) continue
    const existingIndex = indexById.get(id)
    if (existingIndex == null) {
      const created = {
        id,
        version_id: layout.id,
        parent_node_id: nodePatch.clear_parent ? null : nodePatch.parent_node_id || null,
        source_account_id: nodePatch.source_account_id || null,
        source_parent_account_id: nodePatch.source_parent_account_id || null,
        node_type: nodePatch.node_type || 'group',
        statement_type: nodePatch.statement_type || 'excluded',
        cash_flow_section: nodePatch.clear_cash_flow_section ? null : nodePatch.cash_flow_section || null,
        display_name: nodePatch.display_name || 'New row',
        qbo_name: nodePatch.qbo_name || null,
        qbo_fully_qualified_name: nodePatch.qbo_fully_qualified_name || null,
        account_number: nodePatch.account_number || null,
        qbo_account_type: nodePatch.qbo_account_type || null,
        qbo_account_sub_type: nodePatch.qbo_account_sub_type || null,
        qbo_classification: nodePatch.qbo_classification || null,
        normal_balance: nodePatch.normal_balance || null,
        include_in_reports: nodePatch.include_in_reports !== false,
        sort_order: normalizeQuickBooksFpaSortOrder(nodePatch.sort_order),
        formula_json: nodePatch.formula_json || null,
        metadata: nodePatch.metadata || null,
      } as QuickBooksFpaLayoutNode
      indexById.set(id, nodes.length)
      nodes.push(created)
      continue
    }
    const next = { ...nodes[existingIndex], ...nodePatch } as QuickBooksFpaLayoutNode & { clear_parent?: boolean; clear_cash_flow_section?: boolean; clear_qbo_account_sub_type?: boolean }
    if (nodePatch.clear_parent) next.parent_node_id = null
    if (nodePatch.clear_cash_flow_section) next.cash_flow_section = null
    if (nodePatch.clear_qbo_account_sub_type) next.qbo_account_sub_type = null
    delete next.clear_parent
    delete next.clear_cash_flow_section
    delete next.clear_qbo_account_sub_type
    nodes[existingIndex] = next
  }
  const deleted = new Set(patch.delete_node_ids || [])
  if (deleted.size) nodes = nodes.filter((node) => !deleted.has(node.id))
  return { ...layout, nodes, node_count: nodes.length, updated_at: new Date().toISOString() }
}

export type CfoFinancialsPageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  selectedEndPeriod?: string | null
  onSelectedEndPeriodChange?: (value: string | null) => void
  periodOptions?: CfoShellSelectOption[]
  onPeriodOptionsDiscovered?: (options: CfoShellSelectOption[]) => void
  comparisonKind?: MetricComparisonKind
  onComparisonKindChange?: (value: MetricComparisonKind) => void
  comparisonOptions?: CfoShellSelectOption[]
  onNavSelect: (navId: string) => void
  onOpenClassic: () => void
  classicEscapes?: boolean
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

export function CfoFinancialsPage({
  appApi,
  workerClient = null,
  workbookId,
  selectedEndPeriod = null,
  onSelectedEndPeriodChange,
  periodOptions: globalPeriodOptions = [],
  onPeriodOptionsDiscovered,
  comparisonKind = 'mom',
  onComparisonKindChange,
  comparisonOptions: globalComparisonOptions = [],
  onNavSelect,
  navProfile = 'full',
  runtimeProfile,
}: CfoFinancialsPageProps) {
  const analysis = useCfoAnalysis()
  const [statementKind, setStatementKind] = useState<StatementKind>('profit_and_loss')
  const [unitMode, setUnitMode] = useState<UnitMode>('currency_percent')
  const [statementViewMode, setStatementViewMode] = useState<StatementViewMode>('compare')
  const [periodDisplayMode, setPeriodDisplayMode] = useState<PeriodDisplayMode>('months')
  const [displayPeriodCount, setDisplayPeriodCount] = useState(12)
  const [statementRollupDepth, setStatementRollupDepth] = useState(1)
  const [statement, setStatement] = useState<QuickBooksFpaStatementRender | null>(null)
  const [statementLoading, setStatementLoading] = useState(false)
  const [statementError, setStatementError] = useState('')
  const [statementFullscreen, setStatementFullscreen] = useState(false)
  const [excelExportBusy, setExcelExportBusy] = useState(false)
  const [excelExportStatus, setExcelExportStatus] = useState('')
  const [glDetailRequest, setGlDetailRequest] = useState<CfoStatementGlDetailRequest | null>(null)
  const [annotations, setAnnotations] = useState<StatementAnnotations | null>(null)
  const [annotationsLoading, setAnnotationsLoading] = useState(false)
  const [annotationsRunning, setAnnotationsRunning] = useState(false)
  const [cmAnnotations, setCmAnnotations] = useState<StatementAnnotations | null>(null)
  const [flagModalLine, setFlagModalLine] = useState<FlagLine | null>(null)
  const [trustModalOpen, setTrustModalOpen] = useState(false)
  const [statementFocus, setStatementFocus] = useState<{ nodeId: string | null; nonce: number }>({
    nodeId: null,
    nonce: 0,
  })
  const [annotationsRefresh, setAnnotationsRefresh] = useState(0)
  const [layoutOverride, setLayoutOverride] = useState<QuickBooksFpaLayoutVersion | null>(null)
  const [layoutEditStatus, setLayoutEditStatus] = useState('')
  const [layoutEditBusy, setLayoutEditBusy] = useState(false)
  const [layoutRevision, setLayoutRevision] = useState(0)
  const [serviceLineRollups, setServiceLineRollups] = useState<ServiceLineRollup[]>([])
  const [serviceLineSuggestions, setServiceLineSuggestions] = useState<SuggestedServiceLineRollup[]>([])
  const [serviceLinesSeeded, setServiceLinesSeeded] = useState(true)
  const [serviceLineConfigOpen, setServiceLineConfigOpen] = useState(false)
  const [serviceLineConfigStartSuggestions, setServiceLineConfigStartSuggestions] = useState(false)
  const [serviceLineDetecting, setServiceLineDetecting] = useState(false)
  const { bootstrap, cacheState, analyticsReady, bootstrapLoading } = useCfoBootstrap(appApi, workbookId)
  // A missing provider marker is the one-release QuickBooks compatibility
  // contract used by older bootstrap payloads and embedded clients.
  const isQuickBooksProvider = !bootstrap?.accounting_provider || bootstrap.accounting_provider === 'quickbooks'
  const analyticsStatus = typeof bootstrap?.cfo_analytics_status === 'string' ? bootstrap.cfo_analytics_status.trim() : ''
  // Reading the bootstrap starts no work: describe what is served and what
  // brings newer data, never an update that is not running.
  const analyticsState = bootstrap ? cfoAnalyticsState(bootstrap) : null
  const analyticsNotice = analyticsState === 'stale_showing_previous'
    ? 'Showing the last completed analytics. A report update is required to include the latest QuickBooks data.'
    : analyticsState === 'binding_setup'
      ? trimStr(bootstrap?.binding_setup?.message) || analyticsStatus || 'CFO reports are not set up for this workbook yet.'
      : analyticsState === 'needs_report_update' || analyticsReady === false
        ? 'A report update is required to prepare this month’s analytics.'
        : ''
  const connectionId = trimStr(bootstrap?.connection_id)
  const bootstrapSelectedLayoutId = trimStr(bootstrap?.selected_layout_id) || trimStr(bootstrap?.bindings?.[0]?.layout_version_id)
  const selectedLayout = useMemo(
    () => (layoutOverride?.id === bootstrapSelectedLayoutId ? layoutOverride : bootstrap?.layouts?.find((layout) => layout.id === bootstrapSelectedLayoutId) || layoutOverride || null),
    [bootstrap?.layouts, bootstrapSelectedLayoutId, layoutOverride],
  )
  const selectedLayoutId = selectedLayout?.id || bootstrapSelectedLayoutId
  const selectedLayoutName = trimStr(selectedLayout?.name)
  const statementFormatting = useMemo<QuickBooksFpaStatementFormatting>(
    () => ({
      theme: 'minimal',
      scale: 'thousands',
      decimals: 1,
      negativeFormat: 'parentheses',
      zeroDisplay: 'dash',
      showPercentOfSales: false,
      showAccountCodes: true,
      showDetailRows: true,
      showTotalColumn: true,
    }),
    [],
  )

  const statementPeriodOptions = useMemo(
    () =>
      Array.from(new Set((statement?.periods || []).map(normalizeFinancialPeriod).filter(Boolean)))
        .sort((a, b) => b.localeCompare(a))
        .map((period) => ({
          id: period,
          label: formatAsOf(period),
        })),
    [statement],
  )
  const resolvedPeriodOptions = useMemo(
    () => mergeFinancialPeriodOptions(globalPeriodOptions, statementPeriodOptions),
    [globalPeriodOptions, statementPeriodOptions],
  )
  const resolvedPeriodIds = useMemo(
    () => new Set(resolvedPeriodOptions.map((option) => option.id)),
    [resolvedPeriodOptions],
  )
  const normalizedSelectedEndPeriod = normalizeFinancialPeriod(selectedEndPeriod)
  const effectiveStatementEndPeriod = resolvedPeriodOptions.length
    ? resolvedPeriodIds.has(normalizedSelectedEndPeriod)
      ? normalizedSelectedEndPeriod
      : resolvedPeriodOptions[0]?.id || null
    : null

  const selectedComparison = useMemo(
    () => COMPARISON_OPTIONS.find((option) => option.id === comparisonKind) || COMPARISON_OPTIONS[0],
    [comparisonKind],
  )
  const periodCountBounds = periodDisplayBounds(periodDisplayMode)
  const effectiveDisplayPeriodCount = clampPeriodCount(periodDisplayMode, displayPeriodCount)
  const statementDisplayMode = statementViewMode === 'compare' ? selectedComparison.statementMode : periodDisplayStatementMode(periodDisplayMode)
  const statementFetchMonths = periodDisplayFetchMonths(periodDisplayMode, effectiveDisplayPeriodCount)

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (statementFullscreen) {
      document.body.setAttribute('data-cfo-financials-fullscreen', 'true')
    } else {
      document.body.removeAttribute('data-cfo-financials-fullscreen')
    }
    return () => {
      document.body.removeAttribute('data-cfo-financials-fullscreen')
    }
  }, [statementFullscreen])

  useEffect(() => {
    const next = clampPeriodCount(periodDisplayMode, displayPeriodCount)
    if (next !== displayPeriodCount) setDisplayPeriodCount(next)
  }, [displayPeriodCount, periodDisplayMode])

  useEffect(() => {
    let cancelled = false
    if (!connectionId || !selectedLayoutId) {
      setStatement(null)
      return
    }
    setStatementLoading(true)
    setStatementError('')
    const loadStatement = async () => {
      const canUsePageContract =
        workbookId &&
        statementFetchMonths <= 24 &&
        (typeof (appApi as any).getCfoStatementsRender === 'function' || typeof appApi.getQuickBooksCfoStatementsRender === 'function')
      if (canUsePageContract) {
        const getStatements = typeof (appApi as any).getCfoStatementsRender === 'function'
          ? (appApi as any).getCfoStatementsRender.bind(appApi)
          : appApi.getQuickBooksCfoStatementsRender.bind(appApi)
        const contract = await getStatements(workbookId, {
          asOf: effectiveStatementEndPeriod,
          grain: 'month',
        })
        return statementFromRenderContract(contract, statementKind)
      }
      return appApi.renderQuickBooksFpaStatement({
        connection_id: connectionId,
        layout_version_id: selectedLayoutId,
        workbook_id: workbookId,
        statement_type: statementKind,
        end_date: effectiveStatementEndPeriod,
        months: statementFetchMonths,
        formatting: statementFormatting,
      })
    }
    loadStatement()
      .then((rendered) => {
        if (!cancelled) setStatement(rendered)
      })
      .catch((err: any) => {
        if (cancelled) return
        setStatement(null)
        setStatementError(trimStr(err?.message || err) || 'Could not load the native statement grid.')
      })
      .finally(() => {
        if (!cancelled) setStatementLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [analyticsStatus, appApi, connectionId, effectiveStatementEndPeriod, layoutRevision, selectedLayoutId, statementFetchMonths, statementFormatting, statementKind, workbookId])

  useEffect(() => {
    const canonicalAsOf = trimStr(statement?.periods?.[statement.periods.length - 1] || '')
    if (!selectedEndPeriod && canonicalAsOf) onSelectedEndPeriodChange?.(canonicalAsOf)
  }, [onSelectedEndPeriodChange, selectedEndPeriod, statement])

  useEffect(() => {
    if (!resolvedPeriodOptions.length || !onSelectedEndPeriodChange) return
    if (!normalizedSelectedEndPeriod || !resolvedPeriodIds.has(normalizedSelectedEndPeriod)) {
      onSelectedEndPeriodChange(resolvedPeriodOptions[0]?.id || null)
    }
  }, [normalizedSelectedEndPeriod, onSelectedEndPeriodChange, resolvedPeriodIds, resolvedPeriodOptions])

  const asOfLabel = useMemo(
    () => formatAsOf(effectiveStatementEndPeriod || selectedEndPeriod || statement?.periods[statement.periods.length - 1] || null),
    [effectiveStatementEndPeriod, selectedEndPeriod, statement],
  )
  useEffect(() => {
    onPeriodOptionsDiscovered?.(statementPeriodOptions)
  }, [onPeriodOptionsDiscovered, statementPeriodOptions])
  const resolvedPeriodValue =
    (normalizedSelectedEndPeriod && resolvedPeriodOptions.some((option) => option.id === normalizedSelectedEndPeriod)
      ? normalizedSelectedEndPeriod
      : undefined) ||
    resolvedPeriodOptions[0]?.id
  const duckDbOpts = useMemo(() => buildCfoDuckDbOpts(workbookId), [workbookId])

  // Service-line rollups: load (and first-run seed) for the active layout, then keep in sync after edits.
  const refreshServiceLines = useCallback(async () => {
    if (!selectedLayoutId || !isQuickBooksProvider) return
    try {
      const list = await appApi.listServiceLineRollups(selectedLayoutId)
      setServiceLinesSeeded(list.seeded)
      // Seed when never seeded, OR when seeded-but-empty (the server re-applies strong pairings in that
      // case so a genuine service-line client is always auto-loaded). Curated layouts (>=1 rollup) skip.
      if (!list.seeded || list.rollups.length === 0) {
        const seeded = await appApi.seedServiceLineRollups(selectedLayoutId)
        setServiceLineRollups(seeded.rollups)
        setServiceLineSuggestions(seeded.suggestions)
        setServiceLinesSeeded(true)
      } else {
        setServiceLineRollups(list.rollups)
        try {
          const suggested = await appApi.suggestedServiceLineRollups(selectedLayoutId)
          setServiceLineSuggestions(suggested.suggestions)
        } catch {
          setServiceLineSuggestions([])
        }
      }
    } catch {
      setServiceLineRollups([])
      setServiceLineSuggestions([])
    }
  }, [appApi, isQuickBooksProvider, selectedLayoutId])

  useEffect(() => {
    setServiceLineRollups([])
    setServiceLineSuggestions([])
    if (selectedLayoutId && statementKind === 'profit_and_loss' && isQuickBooksProvider) void refreshServiceLines()
  }, [isQuickBooksProvider, refreshServiceLines, selectedLayoutId, statementKind, layoutRevision])

  // Manual "Detect service lines" — force-re-runs detection and applies fresh strong pairings.
  const detectServiceLines = useCallback(async () => {
    if (!selectedLayoutId) return
    setServiceLineDetecting(true)
    try {
      const result = await appApi.seedServiceLineRollups(selectedLayoutId, true)
      setServiceLineRollups(result.rollups)
      setServiceLineSuggestions(result.suggestions)
      setServiceLinesSeeded(true)
    } catch {
      // leave existing state; the panel surfaces the empty/config affordances
    } finally {
      setServiceLineDetecting(false)
    }
  }, [appApi, selectedLayoutId])

  // Inline "Direct CM%" badge: map each rollup's income node(s) to the line's windowed contribution margin.
  const serviceLineIncomeBadge = useMemo(() => {
    const map = new Map<string, { pct: number | null }>()
    if (!statement || statementKind !== 'profit_and_loss' || serviceLineRollups.length === 0) return map
    const rowByNode = new Map<string, QuickBooksFpaStatementRow>()
    for (const row of statement.rows) rowByNode.set(row.node_id, row)
    const { currentIndices } = statementComparisonWindow(statement, resolvedPeriodValue, comparisonKind)
    const windowSum = (ids: string[]): number | null => {
      let total = 0
      let found = false
      for (const id of ids) {
        const row = rowByNode.get(id)
        if (!row) continue
        for (const idx of currentIndices) {
          const v = statementRolledValue(statement, row, idx)
          if (v != null) { total += v; found = true }
        }
      }
      return found ? total : null
    }
    for (const rollup of serviceLineRollups) {
      const rev = windowSum(rollup.income_node_ids)
      const cogs = windowSum(rollup.cogs_node_ids)
      const pct = rev != null && Math.abs(rev) > 0.005 ? (rev - (cogs || 0)) / rev : null
      for (const id of rollup.income_node_ids) map.set(id, { pct })
    }
    return map
  }, [statement, statementKind, serviceLineRollups, resolvedPeriodValue, comparisonKind])

  const statementRowBadge = useMemo(() => {
    if (serviceLineIncomeBadge.size === 0) return undefined
    return (row: QuickBooksFpaStatementRow): ReactNode => {
      const badge = serviceLineIncomeBadge.get(row.node_id)
      if (!badge || badge.pct == null) return null
      const strong = badge.pct >= 0.7
      return (
        <span
          title="Direct contribution margin (revenue − its paired COGS)"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 11,
            fontWeight: 500,
            padding: '2px 7px',
            borderRadius: 999,
            background: strong ? '#E1F5EE' : '#FAEEDA',
            color: strong ? '#0F6E56' : '#854F0B',
          }}
        >
          Direct CM {(badge.pct * 100).toFixed(1)}%
        </span>
      )
    }
  }, [serviceLineIncomeBadge])

  // --- AI annotations (headline + per-line flags) -------------------------------------------------
  const annotationPeriod = useMemo(() => {
    const p = trimStr(resolvedPeriodValue)
    return /^\d{4}-\d{2}-\d{2}$/.test(p) ? `${p.slice(0, 7)}-01` : null
  }, [resolvedPeriodValue])
  const annotationsStatement =
    statementKind === 'profit_and_loss' || statementKind === 'balance_sheet' ? statementKind : null

  useEffect(() => {
    if (!workbookId || !annotationPeriod || !annotationsStatement || !isQuickBooksProvider) {
      setAnnotations(null)
      return
    }
    let cancelled = false
    setAnnotationsLoading(true)
    appApi
      .getQuickBooksFinancialsAiAnnotations(workbookId, {
        statement: annotationsStatement,
        period: annotationPeriod,
        layoutVersionId: selectedLayoutId,
      })
      .then((r) => {
        if (!cancelled) setAnnotations(r)
      })
      .catch(() => {
        if (!cancelled) setAnnotations(null)
      })
      .finally(() => {
        if (!cancelled) setAnnotationsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, workbookId, annotationPeriod, annotationsStatement, isQuickBooksProvider, selectedLayoutId, annotationsRefresh])

  // Mirror "a review exists" into a ref so the auto-run poll loop below can stop the instant the
  // review lands, without re-subscribing to `annotations` on every poll.
  const annotationsHasReportRef = useRef(false)
  useEffect(() => {
    annotationsHasReportRef.current = annotations?.has_report === true
  }, [annotations])

  // --- On-view history self-correction ------------------------------------------------------------
  // A close review is a snapshot of the books when it ran, and entries keep posting after month end.
  // When the viewer opens a month whose books moved since its review (server-computed `data_stale`,
  // or undeterminable on a legacy row), ask the server to re-check: an unchanged month resolves in
  // seconds with no rewrite; a changed month returns `regenerating` — we show an in-band notice and
  // poll until the rewritten review lands (its `generated_at` moves).
  const [closeUpdating, setCloseUpdating] = useState(false)
  const closeUpdatingSinceRef = useRef<string | null>(null)
  const closeRefreshAttemptedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    setCloseUpdating(false)
  }, [annotationPeriod, annotationsStatement, workbookId])
  useEffect(() => {
    if (!workbookId || !annotationPeriod || !annotations?.has_report || !isQuickBooksProvider) return
    if (annotations.status === 'attested') return
    if (annotations.data_stale === false) return
    if (typeof appApi.refreshQuickBooksCloseReview !== 'function') return
    const key = `${workbookId}:${annotationPeriod}`
    if (closeRefreshAttemptedRef.current.has(key)) return
    closeRefreshAttemptedRef.current.add(key)
    const generatedBefore = trimStr(annotations.generated_at) || null
    let cancelled = false
    appApi
      .refreshQuickBooksCloseReview(workbookId, annotationPeriod)
      .then((r) => {
        if (cancelled) return
        const status = trimStr(r?.status)
        if (status === 'regenerating' || status === 'already_running') {
          closeUpdatingSinceRef.current = generatedBefore
          setCloseUpdating(true)
        }
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [annotations, annotationPeriod, appApi, isQuickBooksProvider, workbookId])
  // While a rewrite is in flight, poll the annotations until the new review lands (or give up after
  // ~2 minutes — the band simply keeps the old content).
  useEffect(() => {
    if (!closeUpdating) return
    const timer = window.setInterval(() => setAnnotationsRefresh((n) => n + 1), 4000)
    const timeout = window.setTimeout(() => setCloseUpdating(false), 120000)
    return () => {
      window.clearInterval(timer)
      window.clearTimeout(timeout)
    }
  }, [closeUpdating])
  useEffect(() => {
    if (!closeUpdating) return
    const gen = trimStr(annotations?.generated_at)
    if (gen && gen !== (closeUpdatingSinceRef.current || '')) setCloseUpdating(false)
  }, [annotations, closeUpdating])

  // --- On-demand review backfill for un-reviewed months -------------------------------------------
  // Selecting a period whose month never got a close review (months predating the close engine)
  // used to dead-end at a manual "Run AI review" prompt. Ask the server to author one; while it
  // writes we show the generating state and poll until the review lands. Server-side guards (spend
  // caps, per-month advisory lock, closed-months-only) keep this safe to fire on view.
  const [closeBackfilling, setCloseBackfilling] = useState(false)
  const closeBackfillAttemptedRef = useRef<Set<string>>(new Set())
  const annotationsHasReport = annotations?.has_report
  useEffect(() => {
    setCloseBackfilling(false)
  }, [annotationPeriod, annotationsStatement, workbookId])
  useEffect(() => {
    if (!workbookId || !annotationPeriod || !annotationsStatement || !isQuickBooksProvider) return
    if (annotationsLoading || annotationsHasReport !== false) return
    if (typeof appApi.refreshQuickBooksCloseReview !== 'function') return
    const key = `${workbookId}:${annotationPeriod}`
    if (closeBackfillAttemptedRef.current.has(key)) return
    closeBackfillAttemptedRef.current.add(key)
    appApi
      .refreshQuickBooksCloseReview(workbookId, annotationPeriod)
      .then((r) => {
        const status = trimStr(r?.status)
        if (status === 'regenerating' || status === 'already_running') setCloseBackfilling(true)
      })
      .catch(() => undefined)
  }, [annotationsHasReport, annotationsLoading, annotationPeriod, annotationsStatement, appApi, isQuickBooksProvider, workbookId])
  // Poll while the backfill authors the review; stop when it lands or after ~3 minutes.
  useEffect(() => {
    if (!closeBackfilling) return
    const timer = window.setInterval(() => setAnnotationsRefresh((n) => n + 1), 4000)
    const timeout = window.setTimeout(() => setCloseBackfilling(false), 180000)
    return () => {
      window.clearInterval(timer)
      window.clearTimeout(timeout)
    }
  }, [closeBackfilling])
  useEffect(() => {
    if (closeBackfilling && annotations?.has_report) setCloseBackfilling(false)
  }, [annotations, closeBackfilling])

  useEffect(() => {
    if (!workbookId || !annotationPeriod || statementKind !== 'profit_and_loss' || serviceLineRollups.length === 0 || !isQuickBooksProvider) {
      setCmAnnotations(null)
      return
    }
    let cancelled = false
    appApi
      .getQuickBooksFinancialsAiAnnotations(workbookId, {
        statement: 'contribution_margin',
        period: annotationPeriod,
        layoutVersionId: selectedLayoutId,
      })
      .then((r) => {
        if (!cancelled) setCmAnnotations(r)
      })
      .catch(() => {
        if (!cancelled) setCmAnnotations(null)
      })
    return () => {
      cancelled = true
    }
  }, [appApi, workbookId, annotationPeriod, statementKind, isQuickBooksProvider, selectedLayoutId, serviceLineRollups.length, annotationsRefresh])

  // AI nodes (drivers + per-line flags) reference rows under several id conventions — bare layout uuids,
  // `group:<uuid>`, `rollup:*`, `cash_flow:<section>:<uuid>`, plus a `:source:<id>` suffix. The statement
  // grid keys rows by its own render node_id. Build an index that maps every plausible key → the actual
  // row node_id so drivers deep-link and flags badge the right row regardless of which form the AI used.
  const resolveStatementNode = useMemo(() => {
    const norm = (id: string): string =>
      id
        .replace(/^group:/, '')
        .replace(/^rollup:/, '')
        .replace(/^cash_flow:[^:]+:/, '')
        .replace(/:source:.+$/, '')
    const index = new Map<string, string>()
    for (const r of statement?.rows || []) {
      const actual = r.node_id
      const add = (k?: string | null) => {
        if (k && !index.has(k)) index.set(k, actual)
      }
      add(actual)
      add(norm(actual))
      add(r.account_id as string | null | undefined)
      add(r.account_number as string | null | undefined)
    }
    return (annNodeId: string | null | undefined): string | null => {
      if (!annNodeId) return null
      return index.get(annNodeId) || index.get(norm(annNodeId)) || null
    }
  }, [statement])

  // Per-row flags: each flagged line keyed by node_id, PLUS an aggregate flag propagated up to every
  // ancestor — so a flagged deep/collapsed line still surfaces on its visible level-1 parent.
  const flagByNode = useMemo(() => {
    const out = new Map<string, { line: FlagLine; aggregate: boolean; count: number }>()
    const lines = annotations?.lines
    if (!Array.isArray(lines) || annotations?.layout_stale) return out
    const sevRank = (s?: string) => (s === 'high' ? 3 : s === 'medium' ? 2 : 1)
    const direct = new Set<string>()
    for (const l of lines as FlagLine[]) {
      if (!l?.node_id) continue
      // Resolve the AI's node_id (which may be `group:<uuid>` etc.) to the grid's actual row id.
      const actual = resolveStatementNode(l.node_id) || l.node_id
      out.set(actual, { line: { ...l, node_id: actual }, aggregate: false, count: 1 })
      direct.add(actual)
    }
    const parentOf = new Map<string, string | undefined>()
    for (const r of statement?.rows ?? []) {
      parentOf.set(r.node_id, (r.parent_node_id as string | null | undefined) ?? undefined)
    }
    for (const nid of direct) {
      const leaf = out.get(nid)!.line
      let cur = parentOf.get(nid)
      let guard = 0
      while (cur && guard++ < 64) {
        if (!direct.has(cur)) {
          const existing = out.get(cur)
          if (!existing) {
            out.set(cur, { line: leaf, aggregate: true, count: 1 })
          } else if (existing.aggregate) {
            existing.count += 1
            if (sevRank(leaf.severity) > sevRank(existing.line.severity)) existing.line = leaf
          }
        }
        cur = parentOf.get(cur)
      }
    }
    return out
  }, [annotations, statement, resolveStatementNode])

  const combinedRowBadge = useMemo(() => {
    if (serviceLineIncomeBadge.size === 0 && flagByNode.size === 0) return undefined
    return (row: QuickBooksFpaStatementRow): ReactNode => {
      const cm = statementRowBadge ? statementRowBadge(row) : null
      // The P&L rollup grid keys account rows as `<parent>:source:<originalId>`; flags are keyed by the
      // original id, so fall back to the source-stripped id.
      const sourceMatch = /:source:(.+)$/.exec(row.node_id)
      const flag = flagByNode.get(row.node_id) || (sourceMatch ? flagByNode.get(sourceMatch[1]) : undefined)
      if (!cm && !flag) return null
      const annotated = flag && !flag.aggregate && !!flag.line.user_note
      const title = !flag
        ? ''
        : flag.aggregate
          ? `${flag.count} AI-flagged ${flag.count === 1 ? 'line' : 'lines'} inside — click to review`
          : flag.line.finding || 'The AI flagged this line'
      return (
        <>
          {cm}
          {flag ? (
            <button
              type="button"
              title={title}
              aria-label="AI flag — click to review"
              onClick={(e) => {
                e.stopPropagation()
                const row = (statement?.rows || []).find((candidate) => candidate.node_id === flag.line.node_id)
                setFlagModalLine({
                  ...flag.line,
                  drilldown_period: balanceSheetFlagDrilldownPeriod(statement, row, resolvedPeriodValue),
                })
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 18,
                height: 18,
                padding: flag.aggregate && flag.count > 1 ? '0 5px' : 0,
                marginLeft: 6,
                borderRadius: 999,
                border: flag.aggregate ? `1px solid ${flagSevColor(flag.line.severity)}` : 'none',
                cursor: 'pointer',
                background: annotated
                  ? 'rgba(14,124,134,0.16)'
                  : flag.aggregate
                    ? 'transparent'
                    : flagSevBg(flag.line.severity),
                color: annotated ? 'var(--pos)' : flagSevColor(flag.line.severity),
                fontSize: 11,
                lineHeight: 1,
              }}
            >
              {annotated ? '✓' : '✦'}
              {flag.aggregate && flag.count > 1 ? (
                <span style={{ marginLeft: 3, fontSize: 10, fontWeight: 600 }}>{flag.count}</span>
              ) : null}
            </button>
          ) : null}
        </>
      )
    }
  }, [serviceLineIncomeBadge, flagByNode, statementRowBadge, statement, resolvedPeriodValue])

  const openCloseReview = useCallback(() => {
    onNavSelect('close')
  }, [onNavSelect])

  const runAiReview = useCallback(() => {
    if (!workbookId || annotationsRunning || !isQuickBooksProvider) return
    setAnnotationsRunning(true)
    appApi
      .triggerAiRefresh(workbookId)
      .then(() => {
        setAnnotationsRefresh((n) => n + 1)
        window.setTimeout(() => setAnnotationsRefresh((n) => n + 1), 2500)
      })
      .catch(() => undefined)
      .finally(() => setAnnotationsRunning(false))
  }, [annotationsRunning, appApi, isQuickBooksProvider, workbookId])

  // Automatic refresh belongs to the workbook materialization coordinator, which
  // tracks failed runs and retry timing. A statement mount must only observe it:
  // calling the manual trigger here bypassed that policy and restarted the full
  // feature pipeline whenever this page remounted after a failure.
  const autoReviewHandledFor = useRef<string | null>(null)
  useEffect(() => {
    if (!workbookId || autoReviewHandledFor.current === workbookId || !isQuickBooksProvider) return
    if (typeof appApi.getAiRefreshStatus !== 'function') return
    let cancelled = false

    const pollUntilReady = async () => {
      for (let i = 0; i < 24 && !cancelled; i++) {
        await new Promise((resolve) => window.setTimeout(resolve, 5000))
        if (cancelled) return
        // Pull the freshly-written review in (cheap, idempotent) and stop the moment it lands.
        setAnnotationsRefresh((n) => n + 1)
        if (annotationsHasReportRef.current) return
        const s = await appApi.getAiRefreshStatus(workbookId).catch(() => null)
        if (cancelled) return
        // Stop once it's fresh, or the pipeline is no longer running (e.g. the forecast leg failed
        // but the close review — what this page needs — has already been written or skipped).
        if (s && (!s.stale || (s.reason !== 'in_progress' && s.in_progress == null))) return
      }
    }

    void (async () => {
      const status = await appApi.getAiRefreshStatus(workbookId).catch(() => null)
      if (cancelled || !status) return
      if (status.reason === 'no_binding') return
      autoReviewHandledFor.current = workbookId
      if (status.reason !== 'in_progress' && status.in_progress == null) return
      setAnnotationsRunning(true)
      try {
        await pollUntilReady()
      } finally {
        if (!cancelled) setAnnotationsRunning(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [appApi, isQuickBooksProvider, workbookId])

  const openGlForNode = (nodeId: string, periodOverride?: string | null) => {
    if (!statement) return
    const row = statement.rows?.find((r) => r.node_id === nodeId)
    if (!row) return
    const periods = statement.periods || []
    // Prefer an explicit clicked-cell period (matched period-insensitively of
    // day-of-month), then the page's selected end period, then the last column.
    const requestedPeriod = normalizeFinancialPeriod(periodOverride)
    let idx = requestedPeriod
      ? periods.findIndex((p) => normalizeFinancialPeriod(p) === requestedPeriod)
      : -1
    if (idx < 0) idx = periods.indexOf(resolvedPeriodValue)
    if (idx < 0) idx = periods.length - 1
    const period = periods[idx] || resolvedPeriodValue
    setGlDetailRequest({
      nodeId,
      rowLabel: row.label,
      statementType: row.statement_type,
      accountId: row.account_id ?? null,
      accountNumber: row.account_number ?? null,
      accountName: row.label,
      columnKey: `${period}-value`,
      columnLabel: period,
      period,
      value: row.values?.[idx] ?? null,
    })
    setFlagModalLine(null)
  }

  // Inline GL opener for the shared analysis modal: resolve the clicked metric
  // to a statement row and open the bounded GL modal in place (the same path
  // statement cells and flag cards use) instead of navigating to the explorer.
  // Ref-backed so the registered callback stays identity-stable while always
  // reading the current statement/period, even inside a snapshotted session.
  const openGlDetailInlineRef = useRef<(target: CfoAnalysisGlDetailTarget) => boolean>(() => false)
  openGlDetailInlineRef.current = (target) => {
    if (!statement) return false
    const metricId = String(target.metricId || '').trim()
    const accountId = String(target.accountId || '').trim()
    const label = normalizeStatementRowLabel(String(target.metricLabel || '')).toLowerCase()
    const rows = statement.rows || []
    const row =
      (metricId
        ? rows.find((candidate) => candidate.node_id === metricId || financialsSourceNodeId(candidate.node_id) === metricId)
        : null) ||
      (accountId ? rows.find((candidate) => candidate.account_id === accountId) : null) ||
      (label ? rows.find((candidate) => normalizeStatementRowLabel(candidate.label).toLowerCase() === label) : null)
    if (!row?.node_id) return false
    openGlForNode(row.node_id, target.period ?? null)
    return true
  }
  const openGlDetailInline = useCallback(
    (target: CfoAnalysisGlDetailTarget) => openGlDetailInlineRef.current(target),
    [],
  )

  const canFocusNode = useCallback(
    (nodeId: string) => resolveStatementNode(nodeId) != null,
    [resolveStatementNode],
  )
  const focusStatementRow = useCallback(
    (nodeId: string) => {
      const actual = resolveStatementNode(nodeId)
      if (!actual) return
      setStatementFocus((prev) => ({ nodeId: actual, nonce: prev.nonce + 1 }))
    },
    [resolveStatementNode],
  )

  const financialsAnalysisSelection = useMemo(
    () => buildStatementAnalysisSelection(statement, resolvedPeriodValue, selectedComparison.id, selectedComparison.label, statementKind),
    [resolvedPeriodValue, selectedComparison, statement, statementKind],
  )
  const financialsInstantCards = useMemo(
    () => buildFinancialsInstantCards(statement, resolvedPeriodValue, selectedComparison.id, selectedComparison.label, asOfLabel),
    [asOfLabel, resolvedPeriodValue, selectedComparison, statement],
  )
  const financialsPageFactsFingerprint = useMemo(
    () => [
      SURFACE,
      resolvedPeriodValue,
      selectedComparison.id,
      statementPeriodsFingerprint(statement),
    ].filter(Boolean).join(':'),
    [resolvedPeriodValue, selectedComparison.id, statement],
  )
  const financialsAiInsights = useMemo(
    () => buildFinancialsAiInsights(statement, resolvedPeriodValue, selectedComparison.id, selectedComparison.label),
    [resolvedPeriodValue, selectedComparison, statement],
  )

  const exportStatementExcel = async () => {
    if (!connectionId || !selectedLayoutId) {
      setExcelExportStatus('Choose or create a CFO layout before exporting.')
      return
    }
    setExcelExportBusy(true)
    setExcelExportStatus('Exporting Excel...')
    try {
      let statements: Record<CfoCoreExcelStatementType, QuickBooksFpaStatementRender>
      if (workbookId && statementFetchMonths <= 24 && (typeof (appApi as any).getCfoStatementsRender === 'function' || typeof appApi.getQuickBooksCfoStatementsRender === 'function')) {
        const getStatements = typeof (appApi as any).getCfoStatementsRender === 'function'
          ? (appApi as any).getCfoStatementsRender.bind(appApi)
          : appApi.getQuickBooksCfoStatementsRender.bind(appApi)
        const contract = await getStatements(workbookId, {
          asOf: resolvedPeriodValue,
          grain: 'month',
        })
        statements = {
          profit_and_loss: contract.profit_and_loss,
          balance_sheet: contract.balance_sheet,
          cash_flow: contract.cash_flow,
        }
      } else {
        const rendered = await Promise.all(EXCEL_STATEMENT_TYPES.map(async (nextStatementType) => {
          const result = await appApi.renderQuickBooksFpaStatement({
            connection_id: connectionId,
            layout_version_id: selectedLayoutId,
            workbook_id: workbookId,
            statement_type: nextStatementType,
            end_date: resolvedPeriodValue,
            months: statementFetchMonths,
            balance_sheet_mode: nextStatementType === 'balance_sheet' ? 'ending_balance' : undefined,
            formatting: statementFormatting,
          })
          return [nextStatementType, result] as const
        }))
        statements = Object.fromEntries(rendered) as Record<CfoCoreExcelStatementType, QuickBooksFpaStatementRender>
      }
      const fileName = buildCfoCoreExcelFileName(bootstrap?.company_name, resolvedPeriodValue || statement?.periods[statement.periods.length - 1] || null)
      const snapshot = buildCfoCoreThreeStatementExcelSnapshot({
        companyName: bootstrap?.company_name,
        currencyCode: bootstrap?.currency_code,
        asOfDate: resolvedPeriodValue || statement?.periods[statement.periods.length - 1] || null,
        grain: 'month',
        hideZeroRows: true,
        formatting: statementFormatting,
        view: {
          comparisonMode: statementDisplayMode,
          endPeriod: resolvedPeriodValue,
          maxPeriods: effectiveDisplayPeriodCount,
        },
        statements,
      })
      if (workerClient?.export_excel_snapshot) {
        const response = await workerClient.export_excel_snapshot({
          format: 'xlsx',
          file_name: fileName,
          include_calculated_values: true,
          snapshot,
          actor_client_id: 'cfo_financials_statement_export',
          created_at_ms: Date.now(),
        })
        downloadBase64Workbook({
          fileName: response.file_name || fileName,
          contentType: response.content_type,
          packageBase64: response.package_base64,
        })
        setExcelExportStatus(`Exported ${response.file_name || fileName}.`)
      } else {
        // Browser sessions do not have the native workbook worker. Build the
        // exact same OOXML package locally so every accounting provider keeps
        // the governed three-statement Excel delivery surface.
        const { buildSpreadsheetExcelOoxmlPackageFromWorkbookSnapshot } = await import('../../../protocol/spreadsheetExcelOoxmlPackage')
        const exported = buildSpreadsheetExcelOoxmlPackageFromWorkbookSnapshot({ snapshot })
        downloadWorkbookBytes({ fileName, contentType: EXCEL_CONTENT_TYPE, bytes: exported.package_bytes })
        setExcelExportStatus(`Exported ${fileName}.`)
      }
    } catch (err: any) {
      setExcelExportStatus(`Excel export failed: ${trimStr(err?.message || err) || 'Unknown error'}`)
    } finally {
      setExcelExportBusy(false)
    }
  }

  const openStatementGlDetail = (detail: CfoStatementGlDetailRequest) => {
    setGlDetailRequest(detail)
    setExcelExportStatus('')
  }

  const bindWorkbookToLayout = async (layoutId: string) => {
    if (!workbookId || !connectionId) return
    const binding = bootstrap?.bindings?.[0]
    if (binding?.id) {
      await appApi.updateQuickBooksFpaWorkbookStatementBinding(workbookId, binding.id, {
        connection_id: connectionId,
        layout_version_id: layoutId,
        config_json: binding.config_json || {},
      })
      return
    }
    await appApi.createQuickBooksFpaWorkbookStatementBinding(workbookId, {
      connection_id: connectionId,
      layout_version_id: layoutId,
      config_json: {},
    })
  }

  const ensureEditableLayout = async (): Promise<QuickBooksFpaLayoutVersion | null> => {
    if (!selectedLayout) return null
    if (selectedLayout.status === 'draft') return selectedLayout
    setLayoutEditStatus('Cloning published layout for editing...')
    const cloned = await appApi.cloneQuickBooksFpaLayout(selectedLayout.id, { name: `${selectedLayout.name || 'CFO Core layout'} draft` })
    await bindWorkbookToLayout(cloned.layout.id)
    setLayoutOverride(cloned.layout)
    setLayoutRevision((value) => value + 1)
    setLayoutEditStatus('Editing draft layout.')
    return cloned.layout
  }

  const patchStatementLayout = async (patch: CfoStatementLayoutPatch) => {
    const normalizedPatch = normalizeStatementLayoutPatch(patch)
    if (!normalizedPatch.nodes?.length && !normalizedPatch.delete_node_ids?.length) return
    setLayoutEditBusy(true)
    setLayoutEditStatus('Saving layout...')
    try {
      const editableLayout = await ensureEditableLayout()
      if (!editableLayout) throw new Error('No editable CFO layout is available.')
      setLayoutOverride((current) => applyLayoutPatchLocal(current?.id === editableLayout.id ? current : editableLayout, normalizedPatch))
      const out = await appApi.patchQuickBooksFpaLayoutNodes(editableLayout.id, normalizedPatch)
      setLayoutOverride(out.layout)
      setLayoutRevision((value) => value + 1)
      setLayoutEditStatus(out.validation?.ok === false ? `Saved with ${out.validation.errors.length} validation issue(s).` : 'Layout saved.')
    } catch (error: any) {
      setLayoutEditStatus(trimStr(error?.message || error) || 'Layout save failed.')
      throw error
    } finally {
      setLayoutEditBusy(false)
    }
  }

  const validateStatementLayout = async () => {
    if (!selectedLayoutId) return
    setLayoutEditStatus('Validating layout...')
    try {
      const validation = await appApi.validateQuickBooksFpaLayout(selectedLayoutId)
      setLayoutEditStatus(validation.ok ? 'Layout validation passed.' : `Validation found ${validation.errors.length} issue(s).`)
    } catch (error: any) {
      setLayoutEditStatus(trimStr(error?.message || error) || 'Layout validation failed.')
    }
  }

  const publishStatementLayout = async () => {
    if (!selectedLayoutId) return
    setLayoutEditBusy(true)
    setLayoutEditStatus('Publishing layout...')
    try {
      const out = await appApi.publishQuickBooksFpaLayout(selectedLayoutId)
      setLayoutOverride(out.layout)
      setLayoutRevision((value) => value + 1)
      setLayoutEditStatus('Layout published.')
    } catch (error: any) {
      setLayoutEditStatus(trimStr(error?.message || error) || 'Layout publish failed.')
    } finally {
      setLayoutEditBusy(false)
    }
  }

  const renderSelectChip = (
    label: string,
    options: CfoShellSelectOption[],
    value: string | null | undefined,
    onChange: ((value: string) => void) | undefined,
    ariaLabel: string,
    menuAlign: 'start' | 'end' = 'start',
  ) => {
    return (
      <CfoShellSelectChip
        label={label}
        options={options}
        value={value}
        onChange={onChange}
        ariaLabel={ariaLabel}
        className="cfo-financials-select-chip"
        menuAlign={menuAlign}
      />
    )
  }

  const renderStatementRollupControl = () => (
    <label className="cfo-statement-rollup-depth" title="Set the statement rollup depth">
      <span>Rollup</span>
      <input
        type="range"
        min={0}
        max={FINANCIALS_STATEMENT_ROLLUP_MAX_DEPTH}
        step={1}
        value={statementRollupDepth}
        aria-label="Statement rollup depth"
        onChange={(event) => setStatementRollupDepth(clampStatementRollupDepth(Number(event.target.value)))}
        onInput={(event) => setStatementRollupDepth(clampStatementRollupDepth(Number(event.currentTarget.value)))}
      />
      <b>{statementRollupDepth === FINANCIALS_STATEMENT_ROLLUP_MAX_DEPTH ? 'All' : statementRollupDepth}</b>
    </label>
  )

  const renderStatementControls = (fullscreen: boolean) => (
    <div className={fullscreen ? 'cfo-financials-fullscreen-controls' : 'cfo-financials-inline-controls'}>
      {fullscreen ? <div className="cfo-shell-crumb cfo-financials-fullscreen-crumb"><b>Financials</b> · Profit &amp; Loss</div> : null}
      {fullscreen ? renderSelectChip(
        asOfLabel,
        resolvedPeriodOptions,
        resolvedPeriodValue,
        onSelectedEndPeriodChange ? (value) => onSelectedEndPeriodChange(value) : undefined,
        'Select statement end period',
      ) : null}
      {renderSelectChip(
        'Statement',
        STATEMENT_OPTIONS,
        statementKind,
        (value) => setStatementKind(value as StatementKind),
        'Select statement type',
      )}
      {renderSelectChip(
        'Mode',
        STATEMENT_VIEW_OPTIONS,
        statementViewMode,
        (value) => setStatementViewMode(value as StatementViewMode),
        'Select statement view mode',
      )}
      {statementViewMode === 'periods' ? (
        <>
          {renderSelectChip(
            'Period grouping',
            PERIOD_DISPLAY_OPTIONS,
            periodDisplayMode,
            (value) => setPeriodDisplayMode(value as PeriodDisplayMode),
            'Select statement period grouping',
          )}
          <label className="cfo-financials-period-stepper">
            <input
              type="number"
              min={periodCountBounds.min}
              max={periodCountBounds.max}
              value={effectiveDisplayPeriodCount}
              aria-label="Number of statement periods"
              onChange={(event) => setDisplayPeriodCount(clampPeriodCount(periodDisplayMode, Number(event.target.value)))}
            />
            <span>periods</span>
          </label>
        </>
      ) : renderSelectChip(
        selectedComparison.label,
        globalComparisonOptions.length ? globalComparisonOptions : COMPARISON_OPTIONS.map((option) => ({ id: option.id, label: option.label })),
        comparisonKind,
        onComparisonKindChange ? (value) => onComparisonKindChange(value as MetricComparisonKind) : undefined,
        'Select financial comparison',
      )}
      {renderSelectChip(
        'Units',
        UNIT_OPTIONS,
        unitMode,
        (value) => setUnitMode(value as UnitMode),
        'Select statement units',
        'end',
      )}
      {!fullscreen ? renderStatementRollupControl() : null}
      <button
        type="button"
        className="btn icon cfo-statement-fullscreen-toggle"
        aria-label={statementFullscreen ? 'Exit full screen' : 'Open full screen'}
        title={statementFullscreen ? 'Exit full screen' : 'Full screen'}
        onClick={() => setStatementFullscreen((value) => !value)}
      >
        {statementFullscreen ? <Minimize2 size={17} aria-hidden="true" /> : <Maximize2 size={17} aria-hidden="true" />}
      </button>
    </div>
  )

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId="financials"
      activeSection={SURFACE}
      runtimeProfile={runtimeProfile}
      eyebrow="Understand"
      title="Financials"
      hidePageHead
      crumb={
        <>
          <b>Financials</b> · Profit &amp; Loss
        </>
      }
      asOfLabel={asOfLabel}
      periodOptions={resolvedPeriodOptions}
      periodValue={resolvedPeriodValue}
      onPeriodChange={onSelectedEndPeriodChange}
      periodAriaLabel="Select statement end period"
      comparisonLabel={selectedComparison.label}
      comparisonOptions={globalComparisonOptions.length ? globalComparisonOptions : COMPARISON_OPTIONS.map((option) => ({ id: option.id, label: option.label }))}
      comparisonValue={comparisonKind}
      onComparisonChange={(value) => onComparisonKindChange?.(value as MetricComparisonKind)}
      comparisonAriaLabel="Select financial comparison"
      cacheState={cacheState}
      pageLoading={bootstrapLoading}
      pageLoadingLabel="Loading financial statement context…"
      copilotSubtitle="reading this statement"
      copilotPlaceholder="Ask about Financials…"
      copilotIntro="You’re on the P&L. Ask me to break down any line, compare periods, or trace a number to its journal entries."
      askPlaceholder="Ask about this statement…"
      suggestedPrompts={[
        'Analyze this statement and what to expand next',
        'Compare the rollup to the expanded rows',
        'What would be easier to inspect in full screen?',
      ]}
      duckDbOpts={duckDbOpts}
      sourceTables={['fg_cfo_ai_statement_monthly_activity', 'fg_cfo_ai_opex_monthly_activity', 'fg_cfo_ai_gl_detail']}
      analysisSelection={financialsAnalysisSelection}
      instantCards={financialsInstantCards}
      pageFactsFingerprint={financialsPageFactsFingerprint}
      openGlDetail={openGlDetailInline}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
    >
      {({ copilot }) => (
        <>
          {analyticsNotice ? (
            <div className="cfo-shell-notice">{analyticsNotice}</div>
          ) : null}

          <div className="section-title rise cfo-financials-detail-title">
            <h2 className="ff-guide">Financials</h2>
            <span className="meta">statement detail</span>
          </div>
          {annotationsStatement && !statementFullscreen ? (
            <>
              {annotations?.has_report ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    margin: '0 0 10px',
                    flexWrap: 'wrap',
                  }}
                >
                  {annotations?.readiness?.score != null ? (
                    <button
                      type="button"
                      className="pill"
                      onClick={() => setTrustModalOpen(true)}
                      title="See why — and tell the AI if a flagged check is actually fine"
                      style={{
                        padding: '3px 10px',
                        borderRadius: 999,
                        fontSize: 12,
                        border: 'none',
                        cursor: 'pointer',
                        background:
                          annotations.readiness.overall === 'clean'
                            ? 'rgba(14,124,134,0.12)'
                            : 'rgba(192,73,47,0.12)',
                        color: annotations.readiness.overall === 'clean' ? 'var(--pos)' : 'var(--neg)',
                      }}
                    >
                      Books trust: {annotations.readiness.score}/100&nbsp;ⓘ
                    </button>
                  ) : null}
                  <span style={{ flex: 1 }} />
                  <button
                    className="btn ghost"
                    onClick={openCloseReview}
                    style={{ fontSize: 13 }}
                  >
                    {annotations?.status === 'attested' ? '✓ View close receipt' : 'Review close'}
                  </button>
                </div>
              ) : null}
              <CfoStatementAiHeadline
                annotations={annotations}
                loading={annotationsLoading}
                running={annotationsRunning || closeBackfilling}
                updating={closeUpdating}
                onRun={runAiReview}
                canFocus={canFocusNode}
                onDriverClick={focusStatementRow}
              />
              {trustModalOpen && workbookId && annotationPeriod && annotations?.readiness ? (
                <CfoBooksTrustModal
                  appApi={appApi}
                  workbookId={workbookId}
                  period={annotationPeriod}
                  score={annotations.readiness.score ?? 0}
                  overall={annotations.readiness.overall}
                  checks={annotations.readiness.checks ?? []}
                  onClose={() => setTrustModalOpen(false)}
                  onChanged={() => setAnnotationsRefresh((n) => n + 1)}
                />
              ) : null}
            </>
          ) : null}
          <CfoDepthSection
            title="The full statement"
            showLabel="Show the full statement grid"
            defaultOpen
            hideToggle
            hideHeader
            surface={SURFACE}
            workbookId={workbookId}
          >
            {() => {
              const statementFrame = (
                <div className={statementFullscreen ? 'cfo-shell cfo-financials-statement-frame fullscreen' : 'cfo-financials-statement-frame'}>
                  {!statementFullscreen ? (
                    <CfoControlRow>
                      {renderStatementControls(false)}
                      <PinToReportButton buildPayload={() => statementReportPayload(statement)} />
                    </CfoControlRow>
                  ) : null}
                  {statementError ? <div className="cfo-shell-notice error">{statementError}</div> : null}
                  <CfoStatementTable
                    key={statementFullscreen ? 'financials-statement-fullscreen' : 'financials-statement-normal'}
                    statement={statement}
                    loading={statementLoading && !statement}
                    loadingLabel="Rendering financial statement…"
                    percentOfSales={unitMode === 'percent'}
                    showCurrencyAndPercent={unitMode === 'currency_percent'}
                    rowBadge={combinedRowBadge}
                    focusNodeId={statementFocus.nodeId}
                    focusNonce={statementFocus.nonce}
                    endPeriod={resolvedPeriodValue}
                    comparisonMode={statementDisplayMode}
                    maxPeriods={effectiveDisplayPeriodCount}
                    maxRows={600}
                    defaultCollapsed
                    defaultRollupDepth={1}
                    rollupDepth={statementRollupDepth}
                    onRollupDepthChange={setStatementRollupDepth}
                    fullscreen={statementFullscreen}
                    toolbarLeading={statementFullscreen ? renderStatementControls(true) : null}
                    layoutName={statementFullscreen ? selectedLayoutName : null}
                    onOpenGlDetail={openStatementGlDetail}
                    onExplainCell={(cell) => {
                      const selection = buildFinancialsCellAnalysisSelection(
                        statement,
                        cell,
                        resolvedPeriodValue,
                        selectedComparison.id,
                        selectedComparison.label,
                        statementKind,
                      )
                      if (!selection) return
                      void copilot.sendMessage(`Explain ${selection.metric_label} for ${selection.cell_column_label}.`, {
                        selected_metric_id: selection.metric_id,
                        workspace_context: selection,
                      } as any)
                    }}
                    onAnalyzeRow={(row) => {
                      const selection = buildFinancialsRowAnalysisSelection(
                        statement,
                        row,
                        resolvedPeriodValue,
                        selectedComparison.id,
                        selectedComparison.label,
                        statementKind,
                      )
                      if (!selection) return
                      void copilot.sendMessage(`Explain ${selection.metric_label} from the financial statement.`, {
                        selected_metric_id: selection.metric_id,
                        workspace_context: selection,
                      } as any)
                    }}
                    layoutEdit={statementFullscreen && selectedLayout ? {
                      layoutId: selectedLayout.id,
                      layoutName: selectedLayout.name,
                      layoutStatus: selectedLayout.status,
                      nodes: selectedLayout.nodes || [],
                      editable: true,
                      busy: layoutEditBusy,
                      status: layoutEditStatus,
                      onPatch: patchStatementLayout,
                      onValidate: validateStatementLayout,
                      onPublish: publishStatementLayout,
                      onOpenJournalEntry: () => {
                        setStatementFullscreen(false)
                        onNavSelect('adjustments')
                      },
                    } : null}
                    toolbarActions={
                      statementFullscreen ? (
                        <button
                          type="button"
                          className="btn"
                          disabled={excelExportBusy}
                          title="Export all three statements to Excel"
                          onClick={() => void exportStatementExcel()}
                        >
                          <Download size={15} aria-hidden="true" />
                          {excelExportBusy ? 'Exporting...' : 'Export to Excel'}
                        </button>
                      ) : null
                    }
                  />
                  {excelExportStatus ? (
                    <p className={excelExportStatus.startsWith('Excel export failed') ? 'cfo-statement-export-status error' : 'cfo-statement-export-status'}>
                      {excelExportStatus}
                    </p>
                  ) : null}
                </div>
              )
              return statementFullscreen && typeof document !== 'undefined'
                ? createPortal(statementFrame, document.body)
                : statementFrame
            }}
          </CfoDepthSection>
          {statementKind === 'profit_and_loss' && !statementFullscreen && cmAnnotations?.headline?.text ? (
            <div style={{ margin: '14px 0 0' }}>
              <CfoStatementAiHeadline annotations={cmAnnotations} />
            </div>
          ) : null}
          {statementKind === 'profit_and_loss' && !statementFullscreen ? (
            <ServiceLineContributionPanel
              statement={statement}
              endPeriod={resolvedPeriodValue}
              comparisonKind={comparisonKind}
              periodsMode={statementViewMode === 'periods'}
              periodsCount={effectiveDisplayPeriodCount}
              rollups={serviceLineRollups}
              seeded={serviceLinesSeeded}
              suggestionsCount={serviceLineSuggestions.length}
              loading={statementLoading}
              detecting={serviceLineDetecting}
              onDetect={() => { void detectServiceLines() }}
              onConfigure={() => { setServiceLineConfigStartSuggestions(false); setServiceLineConfigOpen(true) }}
              onReviewSuggestions={() => { setServiceLineConfigStartSuggestions(true); setServiceLineConfigOpen(true) }}
            />
          ) : null}
          {serviceLineConfigOpen && selectedLayoutId ? (
            <ServiceLineConfigDialog
              open={serviceLineConfigOpen}
              appApi={appApi}
              layoutId={selectedLayoutId}
              statement={statement}
              rollups={serviceLineRollups}
              suggestions={serviceLineSuggestions}
              startInSuggestions={serviceLineConfigStartSuggestions}
              detecting={serviceLineDetecting}
              onDetect={() => { void detectServiceLines() }}
              onClose={() => setServiceLineConfigOpen(false)}
              onChanged={() => { void refreshServiceLines() }}
            />
          ) : null}
          {glDetailRequest ? (
            <CfoGlDetailModal
              appApi={appApi}
              workerClient={workerClient}
              workbookId={workbookId}
              connectionId={connectionId}
              duckDbOpts={duckDbOpts}
              detail={glDetailRequest}
              onClose={() => setGlDetailRequest(null)}
              onOpenFullGlDetail={() => {
                setPendingCfoGlDetailHandoff(buildGlDetailHandoff(glDetailRequest))
                setGlDetailRequest(null)
                // The bounded modal may be stacked over an analysis session;
                // close it so the full explorer isn't hidden underneath.
                analysis.close()
                onNavSelect('gl_detail')
              }}
            />
          ) : null}
          {flagModalLine && annotationsStatement && annotationPeriod ? (
            <CfoStatementFlagModal
              appApi={appApi}
              workbookId={workbookId || ''}
              statement={annotationsStatement}
              period={annotationPeriod}
              line={flagModalLine}
              onClose={() => setFlagModalLine(null)}
              onAnswered={() => setAnnotationsRefresh((n) => n + 1)}
              onViewGl={openGlForNode}
            />
          ) : null}
          {financialsAiInsights.length && !annotations?.has_report ? (
            <section className="cfo-financials-ai-insights rise" aria-label="AI insights from financials">
              <div className="cfo-financials-ai-insights-head">
                <span>AI insights</span>
                <small>{selectedComparison.label}</small>
              </div>
              <ul>
                {financialsAiInsights.map((insight, index) => (
                  <li key={`financials-ai-insight-${index}`}>{normalizeCurrencyTextCompact(insight)}</li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
    </CfoInteriorPage>
  )
}

export default CfoFinancialsPage
